// Redacts GraphQL operation variables / resolver inputs before they are sent
// off to error monitoring or analytics. They routinely carry credentials
// (e.g. integration `accessToken`s) and customer PII (names, addresses, phones).
//
// Allowlist, not denylist: a denylist (`/token|secret/i`) catches credentials
// but leaves customer PII untouched. Only structurally-safe keys (identifiers,
// versioning, pagination, low-cardinality enums) keep their values; every other
// primitive is redacted. Object/array structure is preserved so payloads stay
// debuggable. Extend the allowlist deliberately — never with a key that could
// hold user-entered or customer data.
const SAFE_VARIABLE_KEYS = new Set([
    // identifiers
    "id",
    "ids",
    "docId",
    "docIds",
    "itemId",
    "itemIds",
    "projectId",
    "quoteId",
    "requirementId",
    "buildingId",
    "organisationId",
    "organizationId",
    "orgId",
    "templateId",
    "versionId",
    "userId",
    "parentId",
    "categoryId",
    "categoryIds",
    "supplierId",
    "productId",
    "channelId",
    // versioning
    "versionNumber",
    "originVersionNumber",
    "revision",
    "revisionNumber",
    // pagination / ordering
    "limit",
    "offset",
    "first",
    "last",
    "before",
    "after",
    "page",
    "pageSize",
    "sortBy",
    "orderBy",
    "sortDirection",
    "direction",
    // low-cardinality control enums
    "type",
    "status",
    "kind",
    "mode",
    "locale",
    "language",
]);
const REDACTED = "[redacted]";
const CIRCULAR = "[circular]";
/**
 * Recursively redact every primitive value whose key is not on the allowlist.
 * Objects and arrays are traversed with their structure preserved; null and
 * undefined leaf values are kept as-is. A null/undefined argument returns an
 * empty object. Cyclic references become "[circular]" so this is safe to call
 * from error paths.
 */
export function scrubGraphqlVariables(variables) {
    if (!variables)
        return {};
    const seen = new WeakSet();
    seen.add(variables);
    return scrubObject(variables, seen);
}
function scrubObject(obj, seen) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        result[key] = scrubValue(value, SAFE_VARIABLE_KEYS.has(key), seen);
    }
    return result;
}
function scrubValue(value, keyAllowed, seen) {
    if (value === null || value === undefined)
        return value;
    if (typeof value === "object") {
        // Cycle guard (mirrors the WeakSet pattern in @msys/logger's serialize).
        // Delete on exit so shared, non-cyclic references are not flagged.
        if (seen.has(value))
            return CIRCULAR;
        seen.add(value);
        const out = Array.isArray(value)
            ? // Array items inherit the allow-status of the key holding the array.
                value.map(item => scrubValue(item, keyAllowed, seen))
            : scrubObject(value, seen);
        seen.delete(value);
        return out;
    }
    return keyAllowed ? value : REDACTED;
}
//# sourceMappingURL=scrubGraphqlVariables.js.map